import React, { useContext, useMemo, useState } from 'react';
import { Badge, Icon, IssuePanel, IssuePanelProps, Button, Alert } from '@aidkitorg/component-library';
import { DocumentSignal, DocumentSignals, SignalResult } from '@aidkitorg/types/lib/survey';
import { classNames, snakeToEnglish } from '../Util';
import InterfaceContext from '../Context';
import { ApplicantContext } from '../Contexts/ApplicantContext';
import { evalDistro } from '@aidkitorg/types/lib/eval';

const icons: Record<Required<DocumentSignals['signals'][number]>['severity'], IssuePanelProps['icon']> = {
  error: <Icon
    name="XCircleIcon"
    color="red"
    title="error"
    role="informative"
    size="lg"
    strokeWidth={1.5}
  />,
  warn: <Icon
    name="ExclamationTriangleIcon"
    color="orange"
    title="warning"
    role="informative"
    size="md"
    strokeWidth={1.5}
  />,
  info: <Icon
    name="InformationCircleIcon"
    color="black"
    title="informational"
    role="informative"
    strokeWidth={1.5}
  />
}

function SignalResultView({ result, signal }: { result: SignalResult, signal: DocumentSignal }) {

  const { lang } = useContext(InterfaceContext);
  const { info } = useContext(ApplicantContext);

  const panelProps: IssuePanelProps = {
    icon: icons[signal.severity ?? 'info'],
    title: snakeToEnglish(signal.name),
    description: signal.description
  };

  let details: React.ReactNode[] = [];
  switch (result.kind) {
    case 'Example Match Result':
      details.push(
        <Alert variant="warning" mode="inline" dismissible={false} className="border-2 border-amber-200 bg-amber-50">
          <div className="flex items-center space-x-2">
            <Icon size="sm" name="LinkIcon" />
            <span className="font-semibold">Matching Sources Found</span>
          </div>
          <ul className="list-disc list-inside p-2">
            {result.examplesMatched.map((e, i) => <li key={i + '_' + e.url} >
              <a href={e.url} target="_blank" className="text-amber-500">{e.description}</a>
            </li>)}
          </ul>
        </Alert>);
      break;
    case 'Entity Match Result':
      const extractedValues = result.extractedValues;
      if (extractedValues?.length) {
        details.push(
          <Alert variant="secondary" mode="inline" dismissible={false}>
            <ul className="list-disc list-inside space-y-1">
              {extractedValues.map(({ value, locations }, i) =>
                <li key={i + '_' + value}>
                  <span>{value}</span>
                  <div>
                    {locations.map((loc, i) =>
                      <Badge
                        key={i + '_' + loc}
                        variant="neutral"
                        style="pill"
                        className="text-xs">
                        {loc}
                      </Badge>)}
                  </div>
                </li>
              )}
            </ul>
          </Alert>
        );
      }
      break;
    default:
      break;
  }

  if (result.location) {
    details.push(<span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded w-fit">{result.location}</span>);
  }

  panelProps.details = <ul>
    {details.map((d, i) => <li key={i}>{d}</li>)}
  </ul>

  switch (signal.kind) {
    case 'Example Match Signal':
      break;
    case 'Entity Match Signal':
      break;
    case 'Freeform Signal':
      panelProps.description = signal.signalMessage[lang ?? 'en']!;
      break;
    default:
      let _: never = signal;
      break;
  }

  return <div className={classNames('-mx-2')}>
    <IssuePanel {...panelProps} />
  </div>;
}

export function SignalList(props: {
  signals: { config: DocumentSignal, result: SignalResult }[],
}) {

  const { lang } = useContext(InterfaceContext);
  const [expanded, setExpanded] = useState(false);
  const [showAllIssues, setShowAllIssues] = useState(false);
  const loadCount = 2;

  const signals = useMemo(() => props.signals.filter(s => s.result.found).map(({ config, result }, i) =>
    <li key={config?.name + '_' + i} className={classNames(i > 0 && "border-t")}>
      <SignalResultView result={result} signal={config} />
    </li>
  ), [props.signals]);

  return <div>
    <ul role="list" className={classNames("border px-2", !signals.length && 'text-center')}>
      <li
        onClick={() => setExpanded(!expanded)}
        className={classNames(expanded && "border-b", "-mx-2 hover:bg-gray-100 hover:cursor-pointer flex items-center space-x-2 p-2")}
      >
        <Icon name="SparklesIcon" type="outline" className="text-blue-500" />
        <span>AI-Powered Signals</span>
        <Badge style="pill" variant={signals.length > 0 ? "error" : "success"}>{signals.length} issues</Badge>
        <div className="flex-grow"></div>
        <Icon name={expanded ? "ChevronUpIcon" : "ChevronDownIcon"} size="sm" />
      </li>
      {!expanded || signals.length ? null : <span>No issues found 🎉</span>}
      {!expanded ? null : showAllIssues ? signals : signals.slice(0, loadCount)}
      {!expanded ? null : signals.length > loadCount && !showAllIssues ?
        <li className="p-2 w-full border-t">
          <Button onClick={() => setShowAllIssues(true)} className="w-full bg-gray-200" variant="unstyled">
            Show More Issues ({signals.length - loadCount} remaining)
          </Button>
        </li>
        : null
      }
    </ul>
  </div>
}
